import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { 
    Gift, Leaf, Tag, Calendar, Star, ChevronRight, HelpCircle, 
    ShoppingBag, Percent, Award, Sprout
} from 'lucide-react';
import { HelpInfoModal } from '../components/HelpInfoModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ShopperDashboardPage() {
    const navigate = useNavigate();
    const { user, profile } = useAuth();
    const { organization } = useOrganization();
    const [offers, setOffers] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHelpModal, setShowHelpModal] = useState(false);

    useEffect(() => {
        fetchShopperData();
    }, []);

    const fetchShopperData = async () => {
        try {
            const [offersRes, eventsRes] = await Promise.all([
                axios.get(`${API}/offers`),
                axios.get(`${API}/events`)
            ]);
            setOffers(offersRes.data.slice(0, 3));
            setEvents(eventsRes.data.slice(0, 3));
        } catch (error) {
            console.error('Error fetching shopper data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get tier info with premium colors
    const tierInfo = {
        Bronze: { color: 'bg-amber-700', textColor: 'text-amber-200', nextTier: 'Silver', pointsNeeded: 500 },
        Silver: { color: 'bg-slate-400', textColor: 'text-slate-100', nextTier: 'Gold', pointsNeeded: 1500 },
        Gold: { color: 'bg-yellow-500', textColor: 'text-yellow-100', nextTier: null, pointsNeeded: null }
    };

    const currentTier = profile?.tier || 'Bronze';
    const currentTierInfo = tierInfo[currentTier];
    const pointsToNext = currentTierInfo.pointsNeeded 
        ? Math.max(0, currentTierInfo.pointsNeeded - (profile?.lifetime_points || 0))
        : null;

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    return (
        <div className="min-h-screen pb-24" style={{ backgroundColor: '#F9F8F6' }} data-testid="shopper-dashboard-page">
            {/* Premium Header - Garden Centre branding */}
            <div className="relative overflow-hidden rounded-b-[2rem]">
                <div 
                    className="px-6 pt-8 pb-14"
                    style={{ 
                        background: 'linear-gradient(135deg, #2D5016 0%, #3D6A1E 50%, #4A7C23 100%)'
                    }}
                >
                    {/* Texture overlay */}
                    <div 
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                        }}
                    />
                    
                    <div className="relative z-10">
                        {/* Top bar */}
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                {/* Canopy Logo */}
                                <img 
                                    src="/canopy-logo.png"
                                    alt="Canopy"
                                    className="h-10 w-auto object-contain rounded-lg"
                                />
                                {/* Organization name */}
                                <div className="border-l border-white/30 pl-3">
                                    <span 
                                        className="text-lg text-white/90 font-medium"
                                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                                    >
                                        {organization?.name || 'Garden Centre'}
                                    </span>
                                </div>
                            </div>
                            <button 
                                onClick={() => setShowHelpModal(true)}
                                className="p-3 hover:bg-white/10 rounded-full transition-colors duration-200 relative flex items-center gap-2"
                                data-testid="help-btn"
                            >
                                <HelpCircle className="w-6 h-6 text-white" />
                                <span className="text-white/80 text-sm font-medium hidden sm:inline">Help</span>
                            </button>
                        </div>
                        
                        {/* Welcome text */}
                        <div className="space-y-1">
                            <p className="text-white/60 text-sm tracking-wide">Good {getGreeting()}</p>
                            <h1 
                                className="text-3xl text-white"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                            >
                                Welcome, {user?.name?.split(' ')[0] || 'Shopper'}
                            </h1>
                        </div>

                        {/* Points Card - Premium */}
                        <div 
                            className="mt-8 bg-white/15 backdrop-blur-sm rounded-2xl p-5 cursor-pointer hover:bg-white/20 transition-all duration-300 border border-white/10"
                            onClick={() => navigate('/member/points')}
                            data-testid="points-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 rounded-2xl bg-lime-400/20 flex items-center justify-center">
                                        <Star className="w-7 h-7 text-lime-300" />
                                    </div>
                                    <div>
                                        <p className="text-white/70 text-sm tracking-wide">Your Points</p>
                                        <p 
                                            className="text-4xl text-white mt-0.5"
                                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                        >
                                            {profile?.points_balance || 0}
                                        </p>
                                    </div>
                                </div>
                                <div className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium ${
                                    currentTier === 'Gold' ? 'bg-yellow-500/20 text-yellow-200' :
                                    currentTier === 'Silver' ? 'bg-slate-400/20 text-slate-200' :
                                    'bg-amber-700/20 text-amber-200'
                                }`}>
                                    <Award className="w-4 h-4" />
                                    {currentTier}
                                </div>
                            </div>
                            
                            {/* Progress to next tier */}
                            {pointsToNext && (
                                <div className="mt-5">
                                    <div className="flex justify-between text-xs text-white/60 mb-2">
                                        <span>Progress to {currentTierInfo.nextTier}</span>
                                        <span>{pointsToNext} points to go</span>
                                    </div>
                                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-lime-400 rounded-full transition-all duration-500"
                                            style={{ 
                                                width: `${Math.min(100, ((profile?.lifetime_points || 0) / currentTierInfo.pointsNeeded) * 100)}%` 
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Garden Companion Card - Prominent */}
            <div className="px-5 -mt-5 relative z-10">
                <button
                    onClick={() => navigate('/assistant')}
                    className="w-full p-5 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] border border-emerald-100"
                    data-testid="ai-garden-companion-card"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                            <Sprout className="w-8 h-8 text-emerald-600" />
                        </div>
                        <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-emerald-800">
                                    Garden Companion
                                </h3>
                                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">
                                    24/7
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                Burston's gardening expert — ask about our store, seasonal planting tips, or anything garden-related
                            </p>
                        </div>
                        <ChevronRight className="w-6 h-6 text-emerald-400" />
                    </div>
                </button>
            </div>

            {/* Quick Actions - Premium Pills */}
            <div className="px-5 mt-4">
                <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_-4px_rgba(45,74,62,0.1)] border border-[#E5E3DF]">
                    <div className="grid grid-cols-3 gap-4">
                        <QuickAction icon={Percent} label="Offers" onClick={() => navigate('/offers')} color="bg-rose-50 text-rose-700" />
                        <QuickAction icon={Calendar} label="Events" onClick={() => navigate('/events')} color="bg-amber-50 text-amber-700" />
                        <QuickAction icon={Gift} label="Rewards" onClick={() => navigate('/member/points')} color="bg-violet-50 text-violet-700" />
                    </div>
                </div>
            </div>

            {/* Current Offers */}
            <div className="px-5 mt-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 
                        className="text-xl text-[#2D4A3E]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                        Member Offers
                    </h2>
                    <button 
                        onClick={() => navigate('/offers')}
                        className="text-sm text-green-700 font-medium flex items-center gap-1 hover:text-green-800 transition-colors"
                    >
                        View all <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="space-y-3">
                    {offers.map((offer) => (
                        <div 
                            key={offer.id}
                            className="bg-white rounded-2xl p-4 border border-[#E5E3DF] flex items-center gap-4 hover:shadow-md transition-all duration-200 cursor-pointer"
                        >
                            <div className="w-14 h-14 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                                <Tag className="w-6 h-6 text-rose-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-[#2D4A3E] truncate">
                                    {offer.title}
                                </h3>
                                <p className="text-sm text-[#686D6A] truncate mt-0.5">
                                    {offer.description}
                                </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#8A9A86]" />
                        </div>
                    ))}
                    {offers.length === 0 && !loading && (
                        <div className="text-center py-8 text-[#686D6A]">
                            No offers available right now
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming Events */}
            <div className="px-5 mt-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 
                        className="text-xl text-[#2D4A3E]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                        What's On
                    </h2>
                    <button 
                        onClick={() => navigate('/events')}
                        className="text-sm text-green-700 font-medium flex items-center gap-1 hover:text-green-800 transition-colors"
                    >
                        View all <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="space-y-3">
                    {events.map((event) => (
                        <div 
                            key={event.id}
                            className="bg-white rounded-2xl p-4 border border-[#E5E3DF] flex items-center gap-4 hover:shadow-md transition-all duration-200 cursor-pointer"
                        >
                            <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-6 h-6 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-[#2D4A3E] truncate">
                                    {event.title}
                                </h3>
                                <p className="text-sm text-[#686D6A] mt-0.5">
                                    {event.start_date_time ? formatDate(event.start_date_time) : event.date_time ? formatDate(event.date_time) : 'Date TBC'}
                                </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#8A9A86]" />
                        </div>
                    ))}
                    {events.length === 0 && !loading && (
                        <div className="text-center py-8 text-[#686D6A]">
                            No upcoming events
                        </div>
                    )}
                </div>
            </div>

            {/* Garden Tips CTA - Premium */}
            <div className="px-5 mt-8 mb-6">
                <div 
                    className="relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer hover:shadow-xl transition-all duration-300"
                    style={{ 
                        background: 'linear-gradient(135deg, #2D5016 0%, #3D6A1E 50%, #4A7C23 100%)'
                    }}
                    onClick={() => navigate('/tips')}
                >
                    {/* Texture overlay */}
                    <div 
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                        }}
                    />
                    <div className="relative z-10 flex items-center justify-between">
                        <div>
                            <h3 
                                className="text-xl mb-2"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                            >
                                Gardening Tips
                            </h3>
                            <p className="text-white/70 text-sm">
                                Expert advice for your garden this season
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                            <Leaf className="w-6 h-6 text-lime-300" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Help Info Modal */}
            <HelpInfoModal 
                isOpen={showHelpModal} 
                onClose={() => setShowHelpModal(false)} 
                organization={organization}
            />
        </div>
    );
}

function QuickAction({ icon: Icon, label, onClick, color, highlight }) {
    return (
        <button 
            onClick={onClick}
            className={`flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-[#F9F8F6] transition-colors duration-200 ${highlight ? 'ring-2 ring-primary/30 ring-offset-1' : ''}`}
        >
            <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center ${highlight ? 'shadow-md' : ''}`}>
                <Icon className="w-5 h-5" />
            </div>
            <span className={`text-xs font-medium ${highlight ? 'text-primary' : 'text-[#4A4D4C]'}`}>{label}</span>
        </button>
    );
}

function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
}

export default ShopperDashboardPage;
